import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CreditCard, Sparkles } from "lucide-react";
import PasswordInput from "../../components/PasswordInput";
import useCotacaoPwa from "./useCotacaoPwa";
import { cotAxios } from "./api";

export default function CotacaoLoginPage() {
  useCotacaoPwa();
  const nav = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [assinarModo, setAssinarModo] = useState(false); // quando true, apos cadastro/login vai direto para checkout
  const [form, setForm] = useState({ nome: "", email: "", senha: "", empresa: "", telefone: "" });
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const iniciarCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await cotAxios.post(`/cotacao/checkout`, {});
      if (data.status === "checkout_criado" && data.init_point) {
        window.location.href = data.init_point;
        return true;
      }
      if (data.status === "em_breve" || data.status === "erro_mp") {
        toast.error(data.message || "Checkout indisponível no momento");
        if (data.whatsapp) window.open(data.whatsapp, "_blank");
        return false;
      }
      toast.error("Resposta inesperada do checkout");
      return false;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao iniciar checkout");
      return false;
    } finally {
      setCheckoutLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = isLogin ? "/cotacao/auth/login" : "/cotacao/auth/register";
      const payload = isLogin
        ? { email: (form.email || "").trim().toLowerCase(), senha: form.senha }
        : { ...form, email: (form.email || "").trim().toLowerCase() };
      const { data } = await cotAxios.post(path, payload);
      localStorage.setItem("cotacao_token", data.token);
      localStorage.setItem("cotacao_user", JSON.stringify(data.user));
      if (assinarModo) {
        toast.success("Conta pronta! Abrindo pagamento...");
        const ok = await iniciarCheckout();
        if (!ok) {
          // fallback: manda para o dashboard, usuario pode assinar depois
          nav("/cotacoes/dashboard");
        }
        return;
      }
      toast.success(isLogin ? "Login realizado!" : "Conta criada!");
      nav("/cotacoes/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  const assinarPlano = async () => {
    const token = localStorage.getItem("cotacao_token");
    if (token) {
      await iniciarCheckout();
      return;
    }
    // Sem trial: entra no modo assinar (cadastro rapido + checkout direto)
    setAssinarModo(true);
    setIsLogin(false);
    toast.info("Preencha os dados abaixo — você será direcionado ao pagamento em seguida.");
    setTimeout(() => {
      const el = document.getElementById("l-nome");
      if (el) el.focus();
    }, 100);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="shadow-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="cotacao-plano-card">
          <CardContent className="pt-5 pb-4 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600" />
              <p className="text-sm font-bold uppercase tracking-wide text-emerald-800">
                Plano Único · CD Cotações
              </p>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-extrabold text-emerald-700">R$ 497</span>
              <span className="text-sm text-emerald-700/80">/mês</span>
            </div>
            <p className="text-xs text-slate-600">
              Assinatura recorrente via Mercado Pago. Cancele quando quiser.
            </p>
            <Button
              onClick={assinarPlano}
              disabled={checkoutLoading}
              className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11"
              data-testid="cotacao-assinar-btn"
            >
              <CreditCard className="h-4 w-4" />
              {checkoutLoading ? "Abrindo checkout..." : "Assinar Agora"}
            </Button>
          </CardContent>
        </Card>

        <Card className="shadow-xl" data-testid="cotacao-login-card">
        <CardHeader className="text-center space-y-1">
          <img src="/logo-compromisso.png" alt="CD Sistema de Cotações" className="mx-auto mb-3 w-20 h-20 rounded-2xl shadow-md" />
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: "Work Sans" }}>Sistema de Cotações</CardTitle>
          <CardDescription>
            {assinarModo
              ? "Preencha seus dados para prosseguir ao pagamento"
              : (isLogin ? "Entre na sua conta" : "Crie sua conta")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="l-nome">Nome</Label>
                  <Input id="l-nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="login-nome" />
                </div>
                <div>
                  <Label htmlFor="l-empresa">Empresa</Label>
                  <Input id="l-empresa" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} data-testid="login-empresa" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="l-email">Email</Label>
              <Input id="l-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="l-senha">Senha</Label>
              <PasswordInput id="l-senha" required value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} data-testid="login-senha" />
            </div>
            <Button type="submit" className={`w-full ${assinarModo ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-700 hover:bg-blue-800'}`} disabled={loading || checkoutLoading} data-testid="login-submit">
              {loading || checkoutLoading
                ? "Processando..."
                : (assinarModo ? "Cadastrar e Ir para o Pagamento" : (isLogin ? "Entrar" : "Criar conta"))}
            </Button>
            <div className="text-center pt-1">
              <button type="button" onClick={() => { setIsLogin(!isLogin); setAssinarModo(false); }} className="text-sm text-blue-700 hover:underline" data-testid="login-toggle">
                {isLogin ? "Não tem conta? Cadastre-se" : "Já tem conta? Entrar"}
              </button>
            </div>
            <div className="text-center">
              <button type="button" onClick={() => nav("/cotacoes")} className="text-xs text-slate-500 hover:underline">
                ← Voltar para a página inicial
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
